import type {EditorState} from '@tiptap/pm/state';
import {CellSelection, Mark, Node, ResolvedPos, Selection, TextSelection} from "./prosemirror-eksporter";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {Editor} from "@tiptap/core";
import {NodeType} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/TipTapEditor";

export function finnMarkIAktivSelection(state: EditorState, markNavn: string): Mark | null {
    const sel = state.selection;
    let mark: Mark | null = null;
    state.doc.nodesBetween(sel.from, sel.to, (node, pos) => {
        mark = node.marks.find(mark => mark.type.name === markNavn) ?? null
        if (erNil(mark)) return;
    })
    return mark;
}

/** Sann dersom ett eller flere tegn i det tekstlige innholdet er valgt/markert */
export function erTekstMarkert(editor: Editor): boolean {
    return (editor.state.selection instanceof TextSelection) &&
      !editor.state.selection.empty;
}

/** Sann dersom en eller flere tabellceller er valgt/markert */
export function erTabellcellerMarkert(editor: Editor): boolean {
    return erIkkeTomCellSelection(editor.state.selection);
}

/** Sann dersom angitt markering er av typen {@link CellSelection} og minst én tabellcell er valgt/markert */
export function erIkkeTomCellSelection(markering: Selection): markering is CellSelection {
    return (markering instanceof CellSelection) &&
      !markering.empty;
}

/** Returnerer antall markerte tabellceller, 0 dersom ingen */
export function antallTabellcellerMarkert(editor: Editor): number {
    if (!erTabellcellerMarkert(editor)) return 0;
    return editor.state.selection.ranges.length;
}

/** Sann dersom angitt markering er på starten av en node */
export function erPåStartAvNode(markering: Selection): boolean {
    if (markering.$anchor.parentOffset > 0 || markering.from !== markering.to)
        return false;
    return true
}

/** Returnerer tallverdien fra streng av typen '<tall><benevnelse>', ved formatfeil eller manglende tall returneres angitt standardverdi */
export function dekodTallMedBenevnelse(tallstreng: string, benevnelse: string, standardverdi: number): number {
    let resultat = new RegExp(`(\\d+)${benevnelse}`).exec(tallstreng.trim());
    if (erNil(resultat)) return standardverdi;
    return parseInt(resultat[1]);
}

/** Returnerer tallverdien fra streng av typen '<tall><benevnelse>', ved formatfeil eller manglende tall returneres null */
export function dekodTallMedBenevnelseMedNullSomFallback(tallstreng: string, benevnelse: string): number | null {
    let resultat = new RegExp(`(\\d+)${benevnelse}`).exec(tallstreng.trim());
    if (erNil(resultat)) return null;
    return parseInt(resultat[1]);
}

/** Sann dersom node identifisert av angitt posisjon er barn (direkte eller indirekte) av
 * node med angitt type */
export function erBarnAvNodeMedType(posisjon: ResolvedPos, type: NodeType): boolean {
    return forelderNoder(posisjon).some(node => node.type.name == type);
}

/** returnerer nærmeste forelder med angitt type av node identifisert av angitt posisjon */
export function nærmesteForelderMedType(posisjon: ResolvedPos, type: NodeType): Node | null {
    //dersom du har tilgang til TipTapEditor kan du bruke `Editor.$pos(posisjon.from).closest(type)`
    return forelderNoder(posisjon).find(node => node.type.name == type) ?? null;
}

/** returnerer alle foreldrene til node identifisert av angitt posisjon.
 * Nærmeste forelder kommer først, siste forelder vil normal være dokument-noden */
export function forelderNoder(posisjon: ResolvedPos): Node[] {
    return [...Array(posisjon.depth)]
      .map((_, i) => posisjon.node(i + 1)) //henter her alle foreldre, med øverste foreldre først
      .reverse(); //snur rekkefølgen slik at nærmeste forelder kommer først i returnert liste
}
